/**
 * HITL-1492 Pillar 3 (Part A / Capture) — the issue-report taxonomy.
 *
 * The category is NOT emitted on the wire: the consumer owns the type -> category
 * mapping and re-derives it. It lives here because the classification panel GROUPS
 * by it, and a flat 19-item list is exactly what buried
 * `Unnecessary Staff Intervention` (last in the Slack form's scrolling dropdown,
 * and ~52% of all reports).
 *
 * HARDCODED ON PURPOSE (HITL-1501). There is no runtime override. Every category is
 * bound to a chip colour and a display label, and every type to a chip display
 * label, so a config-supplied value would render uncoloured and unlabelled. Adding a
 * type is a release, not a flag flip.
 */

/**
 * Declaration order is load-bearing: `groupIssueTypesByCategory` renders groups in
 * this order.
 *
 * `storeBehavior` is FIRST, and that is the whole point rather than an accident.
 * `Unnecessary Staff Intervention` is ~52% of all reports; in any "natural" ordering
 * it lands near the bottom, which is precisely the failure this taxonomy exists to
 * fix. Putting it first keeps ordering a plain declaration rule — no ranker, no
 * scoring, nothing that reshuffles between flags.
 *
 * `other` stays LAST so the escape hatch never precedes a real value.
 */
export enum IssueCategory {
  storeBehavior = 'store_behavior',
  aiEscalation = 'ai_escalation',
  aiOrder = 'ai_order',
  aiResponse = 'ai_response',
  asr = 'asr',
  audio = 'audio',
  pos = 'pos',
  menuConfig = 'menu_config',
  other = 'other',
  unclassified = 'unclassified',
}

export interface IssueTypeDefinition {
  /**
   * The wire value, emitted as `issue_type`.
   *
   * UNPREFIXED. The Slack form's values carried a family prefix (`AI - Missing Item`,
   * `ASR - Incorrect Transcription`) because a flat dropdown had nothing else to say
   * which lane a value belonged to. The payload now emits `issue_category` alongside
   * this, so the prefix would be the same fact twice — and the UI already renders
   * these under a category heading.
   *
   * ⚠️ CONSUMER NOTE: this means the values no longer join byte-exact to historical
   * Slack-form reports. Mapping old to new is mechanical and one-time —
   * `AI - Missing Item` becomes `{ issue_category: 'ai_order', issue_type: 'Missing
   * Item' }` — but it does have to be done. Nothing was ever emitted in the new shape
   * before this change, because the feature has never been enabled.
   */
  value: string;
  category: IssueCategory;
}

/**
 * Internal 19th value — the Slack form has no equivalent. A report is created
 * `Unclassified` on press and stays that way unless the agent classifies it inside
 * the flag window, so this is the absence of a choice rather than a choice: it is
 * never rendered as a selectable chip.
 *
 * TERMINAL, not "not yet triaged" (HITL-1501). The flag panel is the only
 * classification surface, so nothing arrives later to amend an unclassified report.
 * Consumers must not read `issue_type: null` as "a type is still coming".
 */
export const UNCLASSIFIED_ISSUE_TYPE = 'Unclassified';

/**
 * The single escape hatch. The Slack form had no equivalent, so HITL-1502 maps it per
 * workflow like any other type.
 *
 * Distinct from `Unclassified` on purpose. `Unclassified` means "nobody chose";
 * `Not listed` means "an agent looked and none of the 18 fit". Collapsing them loses
 * the difference between an unclassified report and a taxonomy gap — and the
 * alternative an agent reaches for under time pressure is the closest WRONG value,
 * which poisons the distribution undetectably.
 *
 * A detail is NOT required with it (HITL-1501): every chip commits and closes
 * identically, with no blocking branch anywhere. But `Not listed` is the only signal
 * that the taxonomy missed, and being global it does not say WHICH lane missed —
 * the category is the routing key and this one routes nowhere. So `issue_detail` is
 * the only clue to what the absent value should have been, which is why the panel
 * puts that field above the chips rather than at the bottom.
 */
export const NOT_LISTED_ISSUE_TYPE = 'Not listed';

/**
 * The 18 values IN SLACK-FORM ORDER — not alphabetical, not by frequency — plus
 * `Not listed` last.
 *
 * Values are unprefixed (see `IssueTypeDefinition.value`); the family they belonged
 * to now travels as `issue_category`. Order is kept from the form so the mapping back
 * to historical reports stays positional and obvious.
 *
 * NOTE: this array's order is form order; the PANEL renders groups in
 * `IssueCategory` declaration order and members in this order within a group.
 */
export const ISSUE_TYPES: IssueTypeDefinition[] = [
  { value: 'Escalation', category: IssueCategory.aiEscalation },
  { value: 'Missing Item', category: IssueCategory.aiOrder },
  { value: 'Incorrect Item', category: IssueCategory.aiOrder },
  { value: 'Incorrect Modification', category: IssueCategory.aiOrder },
  { value: 'Missing Modification', category: IssueCategory.aiOrder },
  { value: 'Upsell', category: IssueCategory.aiOrder },
  {
    value: 'Missing Response',
    category: IssueCategory.aiResponse,
  },
  {
    value: 'Incorrect Response',
    category: IssueCategory.aiResponse,
  },
  { value: 'No Response', category: IssueCategory.aiResponse },
  {
    value: 'Slow Response',
    category: IssueCategory.aiResponse,
  },
  { value: 'Incorrect Price', category: IssueCategory.aiOrder },
  { value: 'Missing Transcription', category: IssueCategory.asr },
  { value: 'Incorrect Transcription', category: IssueCategory.asr },
  { value: 'Low Volume', category: IssueCategory.audio },
  { value: 'Choppy', category: IssueCategory.audio },
  { value: 'Validation', category: IssueCategory.pos },
  // The only value with no family prefix. Its category is `menu_config`, CONFIRMED
  // 2026-08-19 — it was a considered guess until then.
  //
  // Its MEANING stays ambiguous (did the AI fail to auto-combo, or wrongly
  // auto-combo?). That is a question about what the value means, not where it
  // routes, so it does not reopen the category. An agent who means the second and
  // does not think this fits can pick `Not listed` with a detail.
  { value: 'Autocombo', category: IssueCategory.menuConfig },
  {
    value: 'Unnecessary Staff Intervention',
    category: IssueCategory.storeBehavior,
  },
  { value: NOT_LISTED_ISSUE_TYPE, category: IssueCategory.other },
];

/**
 * Flat `issue_type -> issue_category` map.
 */
export const buildIssueCategoryMap = (): Record<string, IssueCategory> =>
  ISSUE_TYPES.reduce((acc, { value, category }) => {
    acc[value] = category;
    return acc;
  }, {} as Record<string, IssueCategory>);

/**
 * Grouped by category, which is what the classification panel renders — not the flat
 * list. A flat 19-item list is exactly what buried `Unnecessary Staff Intervention`
 * (52% of all reports), so grouping is the fix, not decoration.
 *
 * Category order follows IssueCategory declaration order (storeBehavior first, other
 * last); within a category, form order. Empty categories are omitted, and
 * `unclassified` never appears because it is not a real choice. `other` DOES appear —
 * `Not listed` is a real choice — and its declaration position puts it last.
 */
export const groupIssueTypesByCategory = (): Array<{
  category: IssueCategory;
  types: IssueTypeDefinition[];
}> =>
  (Object.values(IssueCategory) as IssueCategory[])
    .filter((category) => category !== IssueCategory.unclassified)
    .map((category) => ({
      category,
      types: ISSUE_TYPES.filter((type) => type.category === category),
    }))
    .filter((group) => group.types.length > 0);
